import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './photo-upload.style.js';
import { PhotoUploadService, PhotoUploadResult } from '../services/photo-upload.service.js';

@customElement('photo-upload')
export class PhotoUpload extends LitElement {
  @property({ type: String })
  currentPhotoUrl: string | null = null;

  @property({ type: String })
  assetId: string = '';

  @property({ type: String })
  companyId: string = 'default';

  @state()
  private selectedFile: File | null = null;

  @state()
  private previewUrl: string | null = null;

  @state()
  private isUploading = false;

  @state()
  private errorMessage = '';

  @state()
  private dragOver = false;

  private photoUploadService = new PhotoUploadService();

  static styles = styles;

  connectedCallback() {
    super.connectedCallback();
  }

  private handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleFile(input.files[0]);
    }
  }

  private handleFile(file: File) {
    this.selectedFile = file;
    this.errorMessage = '';
    
    // Create preview using URL.createObjectURL
    this.previewUrl = URL.createObjectURL(file);
  }

  private handleDragOver(e: DragEvent) {
    e.preventDefault();
    this.dragOver = true;
  }

  private handleDragLeave(e: DragEvent) {
    e.preventDefault();
    this.dragOver = false;
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    this.dragOver = false;
    
    if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
      this.handleFile(e.dataTransfer.files[0]);
    }
  }

  private handleUploadClick() {
    if (this.selectedFile) {
      this.uploadPhoto();
    } else {
      this.shadowRoot?.querySelector('.file-input')?.click();
    }
  }

  private async uploadPhoto() {
    if (!this.selectedFile || !this.assetId) return;

    try {
      this.isUploading = true;
      this.errorMessage = '';

      const result: PhotoUploadResult = await this.photoUploadService.uploadPhoto(
        this.selectedFile,
        this.assetId,
        this.companyId
      );

      if (result.success && result.url) {
        // Dispatch event with both the new photo URL and the file
        this.dispatchEvent(new CustomEvent('photo-uploaded', {
          detail: { url: result.url, file: this.selectedFile },
          bubbles: true,
          composed: true
        }));

        // Update current photo
        this.currentPhotoUrl = result.url;
        this.previewUrl = result.url;
        this.selectedFile = null;
      } else {
        this.errorMessage = result.error || 'Upload failed';
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      this.errorMessage = error.message || 'Upload failed';
    } finally {
      this.isUploading = false;
    }
  }

  private async removePhoto() {
    if (this.currentPhotoUrl && this.assetId) {
      try {
        // Use the correct method from the service
        const result = await this.photoUploadService.deletePhotoByPath(this.currentPhotoUrl);
        if (result.success) {
          this.dispatchEvent(new CustomEvent('photo-removed', {
            bubbles: true,
            composed: true
          }));
          
          this.currentPhotoUrl = null;
          this.previewUrl = null;
          this.selectedFile = null;
        } else {
          console.error('Failed to remove photo:', result.error);
        }
      } catch (error) {
        console.error('Error removing photo:', error);
      }
    } else {
      this.dispatchEvent(new CustomEvent('photo-removed', {
        bubbles: true,
        composed: true
      }));
      
      this.currentPhotoUrl = null;
      this.previewUrl = null;
      this.selectedFile = null;
    }
  }

  private clearSelection() {
    if (this.previewUrl && this.previewUrl !== this.currentPhotoUrl) {
      // Revoke the object URL to free memory
      URL.revokeObjectURL(this.previewUrl);
    }
    this.selectedFile = null;
    this.previewUrl = this.currentPhotoUrl;
    this.errorMessage = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up preview URLs
    if (this.previewUrl && this.previewUrl !== this.currentPhotoUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  render() {
    return html`
      <div class="photo-upload-container">
        <!-- Current Photo Display -->
        ${this.currentPhotoUrl ? html`
          <div class="current-photo">
            <img src="${this.currentPhotoUrl}" alt="Current asset photo" />
            <sl-button 
              variant="danger" 
              size="small" 
              @click=${this.removePhoto}
              class="remove-photo"
            >
              <sl-icon slot="prefix" name="trash"></sl-icon>
              Remove Photo
            </sl-button>
          </div>
        ` : ''}

        <!-- Error Message -->
        ${this.errorMessage ? html`
          <div class="error-message">
            <sl-icon name="exclamation-triangle"></sl-icon>
            ${this.errorMessage}
          </div>
        ` : ''}

        <!-- Upload Progress -->
        ${this.isUploading ? html`
          <div class="upload-progress">
            <sl-progress-bar indeterminate></sl-progress-bar>
            <p style="text-align: center; margin-top: 0.5rem; color: var(--sl-color-neutral-600);">
              Uploading photo...
            </p>
          </div>
        ` : ''}

        <!-- File Selection -->
        ${!this.selectedFile && !this.currentPhotoUrl ? html`
          <div 
            class="upload-area ${this.dragOver ? 'drag-over' : ''}"
            @dragover=${this.handleDragOver}
            @dragleave=${this.handleDragLeave}
            @drop=${this.handleDrop}
            @click=${this.handleUploadClick}
          >
            <div class="upload-icon">
              <sl-icon name="cloud-upload"></sl-icon>
            </div>
            <div class="upload-text">Click to select or drag & drop a photo</div>
            <div class="upload-hint">JPG, PNG, WebP, GIF up to 5MB</div>
          </div>
        ` : ''}

        <!-- File Preview -->
        ${this.selectedFile ? html`
          <div class="preview-container">
            <img 
              src="${this.previewUrl}" 
              alt="Photo preview" 
              class="preview-image"
            />
            <div class="preview-actions">
              <sl-button 
                variant="primary" 
                size="small" 
                @click=${this.uploadPhoto}
                ?loading=${this.isUploading}
              >
                <sl-icon slot="prefix" name="upload"></sl-icon>
                Upload Photo
              </sl-button>
              <sl-button 
                variant="neutral" 
                size="small" 
                @click=${this.clearSelection}
              >
                Cancel
              </sl-button>
            </div>
          </div>
        ` : ''}

        <!-- Hidden File Input -->
        <input 
          type="file" 
          class="file-input" 
          accept="image/*"
          @change=${this.handleFileSelect}
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'photo-upload': PhotoUpload;
  }
}
